import { Box, Button, SxProps, Typography } from '@mui/material';
import React from 'react';
import { FaOptionIcon, type NamedIconsType } from '@europrocurement/l2d-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    useL2DTheme,
    resolveColorMain,
    computeComponentColors,
} from '@europrocurement/flexy-components/hooks';
import AnimatedCounter from '../AnimatedCounter/AnimatedCounter';

export type ActionButton = {
    label: string;
    onClick: () => void;
    disabled?: boolean;
};

export type StatCardProps = {
    title: string | React.ReactNode;
    icon: NamedIconsType | IconProp;
    data?: string | number;
    paragraph?: string | React.ReactNode;
    color?: string;
    actions?: ActionButton[];
    sx?: SxProps;
    sxData?: SxProps;
};

export const StatCard: React.FunctionComponent<StatCardProps> = function ({
    title,
    icon,
    data,
    paragraph,
    color = 'primary',
    actions = [],
    sx,
    sxData,
}) {
    const { palette } = useL2DTheme();

    const colorMain = resolveColorMain(
        color,
        palette as unknown as import('@mui/material').Theme['palette'],
    );
    const paletteColor = (palette as unknown as Record<string, { main: string; dark?: string }>)[
        color
    ];
    const colorDark = paletteColor?.dark ?? palette.primary.dark;

    const token = computeComponentColors(colorMain, palette.mode);

    const iconBg = token.glassyStyle;
    const buttonTextColor = token.textColor;

    const actionButtonSx = {
        maxHeight: '50px',
        ...token.glassyStyle,
        color: buttonTextColor,
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '14px',
        whiteSpace: 'nowrap',
        borderRadius: '8px',
        boxShadow: 'none',
        '&:hover': {
            ...token.glassyStyleOnHover,
            color: token.textColorOnHover,
            boxShadow: 'none',
        },
    };

    const isNamedIcon = typeof icon === 'object' && icon !== null && 'props' in icon;

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                backgroundColor: palette.background.paper,
                borderRadius: 2,
                boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 2,
                ...sx,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: '100%',
                    alignItems: 'start',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden',
                    mb: 0.5,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-5px',
                        left: 0,
                        right: 0,
                        height: 0,
                        boxShadow: `0 0 10px 14px ${palette.background.paper}`,
                        pointerEvents: 'none',
                        zIndex: 1,
                    },
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 1,
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: 56,
                            height: 56,
                            borderRadius: '10px',
                            ...iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            lineHeight: 0,
                            paddingRight: '8px',
                            paddingBottom: '2px',
                        }}
                    >
                        {isNamedIcon ? (
                            <FaOptionIcon
                                {...(icon as NamedIconsType).props}
                                size="lg"
                                color={colorMain}
                                optionColor={colorDark}
                            />
                        ) : (
                            <FaOptionIcon
                                icon={icon as IconProp}
                                size="lg"
                                color={colorMain}
                                optionColor={colorDark}
                            />
                        )}
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            minWidth: 0,
                            height: 80,
                            width: '100%',
                            overflow: 'hidden',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: '-5px',
                                left: 0,
                                right: 0,
                                height: 0,
                                boxShadow: `0 0 14px 20px ${palette.background.paper}`,
                                pointerEvents: 'none',
                                zIndex: 1,
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: 'text.primary',
                                lineHeight: 1.4,
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100px',
                        height: '100%',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: '-10px',
                    }}
                >
                    {paragraph && (
                        <Box
                            sx={{
                                height: '100%',
                                width: '100%',
                                position: 'relative',
                                ...iconBg,
                                borderRadius: '8px',
                                padding: '10px 14px',
                                overflow: 'hidden',
                                mb: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    color: buttonTextColor,
                                    lineHeight: 1.6,
                                }}
                            >
                                {paragraph}
                            </Typography>
                        </Box>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            ...sxData,
                        }}
                    >
                        <AnimatedCounter
                            value={data}
                            sx={{
                                display: 'block',
                                fontSize: '26px',
                                fontWeight: 600,
                                lineHeight: 1.2,
                                color: 'text.primary',
                                whiteSpace: 'nowrap',
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {actions.length > 0 && (
                <Box
                    sx={{
                        flexShrink: 0,
                        mt: -0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {actions.map((action) => (
                        <Button
                            key={action.label}
                            disabled={action.disabled}
                            onClick={action.onClick}
                            sx={{ ...actionButtonSx, width: '100%', padding: '8px 20px' }}
                        >
                            {action.label}
                        </Button>
                    ))}
                </Box>
            )}
        </Box>
    );
};
